import { useEffect, useRef, useState } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router';
import { getPhotoVogueService, type Photo } from '@/services/photovogue';

export type ModerationChoice = 'approved' | 'rejected' | 'ignore';

const LEAVE_WARNING =
  'Leave moderation? Your unsaved moderation choices will be lost.';

/** Keep a fixed page while saving, so newly loaded photos can never be rejected. */
export const usePageModeration = (onSaved: () => Promise<void>) => {
  const [photos, setPhotos] = useState<Photo[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [ignoredIds, setIgnoredIds] = useState<string[]>([]);
  const [hasChoice, setHasChoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const savingRef = useRef(false);
  const active = photos !== null;
  const blocker = useBlocker(active);

  useBeforeUnload((event) => {
    if (active) {
      event.preventDefault();
      event.returnValue = '';
    }
  });

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (!savingRef.current && window.confirm(LEAVE_WARNING)) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  const finish = () => {
    setPhotos(null);
    setSelectedIds([]);
    setIgnoredIds([]);
    setError(null);
    setRetrying(false);
    setHasChoice(false);
  };

  const discard = () => {
    if (savingRef.current) return false;
    if (active && !window.confirm(LEAVE_WARNING)) return false;
    finish();
    if (retrying) void onSaved();
    return true;
  };

  const start = (page: Photo[]) => {
    if (!page.length || savingRef.current) return;
    setPhotos(page);
    setSelectedIds([]);
    setIgnoredIds([]);
    setError(null);
    setRetrying(false);
    setHasChoice(false);
  };

  const setChoice = (id: string, choice: ModerationChoice) => {
    if (savingRef.current || !photos?.some((photo) => photo.id === id)) return;
    setHasChoice(true);
    setSelectedIds((ids) =>
      choice === 'approved'
        ? [...ids.filter((value) => value !== id), id]
        : ids.filter((value) => value !== id),
    );
    setIgnoredIds((ids) =>
      choice === 'ignore'
        ? [...ids.filter((value) => value !== id), id]
        : ids.filter((value) => value !== id),
    );
  };

  const save = async () => {
    const decisions =
      photos?.filter((photo) => !ignoredIds.includes(photo.id)) ?? [];
    if (!photos?.length || !decisions.length || !hasChoice || savingRef.current)
      return;
    if (
      !window.confirm(
        `Approve ${selectedIds.length} and reject ${decisions.length - selectedIds.length} photos? Ignore ${ignoredIds.length}. Only these ${decisions.length} photos on this page will be updated. Rejected photos will be marked Rejected.`,
      )
    )
      return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const result = await getPhotoVogueService().patchPhotos(
        decisions.map((photo) => ({
          id: photo.id,
          state: selectedIds.includes(photo.id) ? 'approved' : 'rejected',
          types: photo.types,
        })),
      );
      if (result.failed.length) {
        const failedPhotos = decisions.filter((photo) =>
          result.failed.includes(photo.id),
        );
        if (failedPhotos.length !== new Set(result.failed).size) {
          throw new Error('Unrecognized batch result');
        }
        setPhotos(failedPhotos);
        setIgnoredIds([]);
        setSelectedIds(selectedIds.filter((id) => result.failed.includes(id)));
        setRetrying(true);
        setError(
          `${decisions.length - failedPhotos.length} saved. ${failedPhotos.length} could not be saved. Your remaining choices are preserved; retry to save them.`,
        );
      } else {
        finish();
        await onSaved();
      }
    } catch {
      setRetrying(true);
      setError(
        'Unable to confirm the save. Your choices are preserved. Retry will resend these decisions; some photos may already have been saved.',
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return {
    active,
    photos,
    selectedIds,
    ignoredIds,
    setChoice,
    saving,
    error,
    retrying,
    start,
    discard,
    save,
    canSave: Boolean(
      photos?.some((photo) => !ignoredIds.includes(photo.id)) &&
        hasChoice &&
        !saving,
    ),
    toggle: (id: string) =>
      setChoice(id, selectedIds.includes(id) ? 'rejected' : 'approved'),
    selectAll: () => {
      if (savingRef.current) return;
      setHasChoice(true);
      setSelectedIds(photos?.map((photo) => photo.id) ?? []);
      setIgnoredIds([]);
    },
    clear: () => {
      if (savingRef.current) return;
      setHasChoice(true);
      setSelectedIds([]);
      setIgnoredIds([]);
    },
  };
};

export type PageModeration = ReturnType<typeof usePageModeration>;
